import { Button, TextInput } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import React, { ReactNode } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTwilioAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/TwilioAccountProvider/useTwilioAccount";
import { SelectPhoneNumberDialog } from "../../../../components/SelectPhoneNumberDialog";
import { useTwilioPhoneNumberAssociationFlow } from "../../hooks/useTwilioPhoneNumberAssociationFlow";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";

interface TwilioConnectionFormValues {
  sid: string;
  token: string;
}

export const TwilioConnectionForm: React.FC = () => {
  const { t } = useTranslations();
  const { connectTwilioAccount, isConnectingTwilioAccount } =
    useTwilioAccount();

  const {
    control,
    getValues,
    handleSubmit,
    formState: { isValid },
  } = useForm<TwilioConnectionFormValues>({
    mode: "onChange",
    defaultValues: { sid: "", token: "" },
  });

  const {
    isDialogOpen,
    isListingPhoneNumbers,
    phoneNumbers,
    startFlow,
    closeDialog,
    associate,
  } = useTwilioPhoneNumberAssociationFlow({
    associatePhoneNumber: (phoneNumber) => {
      const { sid, token } = getValues();
      return connectTwilioAccount({
        sid: sid.trim(),
        token: token.trim(),
        phone_number: phoneNumber.phone_number,
        phone_number_sid: phoneNumber.sid,
      });
    },
    successMessage: t({
      id: "client-support-page.twilio-account-connected",
      defaultMessage: "Twilio Account was successfully connected",
    }),
  });

  const handleContinue = handleSubmit(({ sid, token }) =>
    startFlow({ sid: sid.trim(), token: token.trim() })
  );

  return (
    <>
      <div className="flex flex-col gap-6 rounded-3xl border border-solid border-dark-100 bg-white p-8 shadow-[0_2px_4px_rgba(0,0,0,0.08)] max-lg:gap-4 max-lg:p-5">
        <div className="flex flex-col gap-1">
          <span className="font-secondary text-lg font-medium text-black max-lg:text-base">
            {t({
              id: "client-support-page.twilio-connection",
              defaultMessage: "Connection",
            })}
          </span>
          <span className="font-secondary text-sm leading-[140%] text-dark-800">
            {t(
              {
                id: "client-support-page.twilio-connection-description",
                defaultMessage:
                  "Enter your account details to connect. You can always find below details in <link>Twilio dashboard</link>",
              },
              {
                link: (text: ReactNode) => (
                  <a
                    className="font-medium text-primary underline"
                    href="https://console.twilio.com"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {text}
                  </a>
                ),
              }
            )}
          </span>
        </div>

        <Controller
          control={control}
          name="sid"
          rules={{ required: true, validate: (value) => !!value.trim() }}
          render={({ field: { onChange, ...props } }) => (
            <TextInput
              {...props}
              onChange={onChange}
              autoComplete="off"
              label={t({
                id: "client-support-page.twilio-account-sid",
                defaultMessage: "Account SID",
              })}
              placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            />
          )}
        />
        <Controller
          control={control}
          name="token"
          rules={{ required: true, validate: (value) => !!value.trim() }}
          render={({ field: { onChange, ...props } }) => (
            <TextInput
              {...props}
              onChange={onChange}
              type="password"
              autoComplete="new-password"
              label={t({
                id: "client-support-page.twilio-auth-token",
                defaultMessage: "Auth Token",
              })}
              placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            />
          )}
        />

        <Button
          className="h-12 w-[220px]"
          isDisabled={!isValid || isListingPhoneNumbers}
          isLoading={isListingPhoneNumbers}
          iconEnd={<ArrowRightIcon />}
          onClick={handleContinue}
        >
          {t({
            id: "client-support-page.twilio-continue",
            defaultMessage: "Continue",
          })}
        </Button>
      </div>

      <SelectPhoneNumberDialog
        isOpen={isDialogOpen}
        isLoading={isListingPhoneNumbers}
        phoneNumbers={phoneNumbers}
        isAssociating={isConnectingTwilioAccount}
        onAssociate={associate}
        onClose={closeDialog}
      />
    </>
  );
};
